import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
const { FiArrowRight, FiMapPin, FiCalendar, FiUsers } = FiIcons;

const Hero = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.3 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const stats = [
    { number: 'UK-Wide', label: 'Coverage', icon: FiMapPin },
    { number: '500+', label: 'Events Listed', icon: FiCalendar },
    { number: '700+', label: 'LGBTQIA+ Businesses', icon: FiUsers },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden" aria-labelledby="hero-heading">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-blue-50" aria-hidden="true" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <motion.div animate={{ y: [-20, 20, -20], rotate: [0, 5, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} className="absolute top-20 right-20 w-64 h-64 rounded-full bg-purple-100 blur-3xl" />
        <motion.div animate={{ y: [20, -20, 20], rotate: [0, -5, 0] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }} className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-blue-100 blur-3xl" />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? "visible" : "hidden"} className="space-y-8">
          <motion.div variants={itemVariants} className="inline-flex">
            <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-2">
              <SafeIcon icon={FiMapPin} className="w-4 h-4" aria-hidden="true" />
              <span>Now serving London, Brighton & Manchester</span>
            </div>
          </motion.div>
          <motion.div variants={itemVariants} className="space-y-6">
            <h1 style={{
      color: 'rgb(17, 24, 39)'
    }} id="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">Find LGBTQIA+ events and businesses instantly.</h1>
            <p className="text-xl sm:text-2xl text-gray-600 font-normal leading-relaxed max-w-3xl mx-auto">
              Discover what's on near you in one message — no multiple directories or apps, no fluff.
            </p>
          </motion.div>
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <a href="https://wa.me/message/ST34ZBAH3RQDH1" target="_blank" rel="noopener noreferrer" className="group bg-purple-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-purple-700 transition-all duration-300 focus-ring flex items-center space-x-2 shadow-lg hover:shadow-xl" aria-describedby="cta-description">
              <span>Get Early Access Now</span>
            </a>
            <button 
              onClick={() => scrollToSection('partner-heading')}
              className="group bg-purple-300 text-purple-800 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-purple-400 transition-all duration-300 focus-ring flex items-center space-x-2 shadow-lg hover:shadow-xl"
              aria-describedby="business-cta-description"
            >
              <span>List Your Event/Business Now</span>
            </button>
            <span id="cta-description" className="sr-only">Get early access to the Togethr members portal</span>
            <span id="business-cta-description" className="sr-only">List your event or business on Togethr</span>
          </motion.div>
          <motion.div variants={itemVariants} className="pt-8">
            <div className="grid grid-cols-3 gap-8 max-w-lg mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-16 h-16 bg-purple-100/50 rounded-2xl flex items-center justify-center">
                      <SafeIcon icon={stat.icon} className="w-8 h-8 text-purple-600" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="text-xl font-bold text-gray-900 mb-1">{stat.number}</div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.6 }} className="absolute bottom-8 left-1/2 transform -translate-x-1/2" aria-label="Scroll down to see more content">
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center">
          <motion.div animate={{ y: [0, 12, 0] }} transition={{ duration: 2, repeat: Infinity }} className="w-1 h-3 bg-gray-400 rounded-full mt-2" />
        </motion.div>
      </motion.div>
    </section>
  );
};
export default Hero;